import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Search, Pill, Package, Check, Plus, Loader2, AlertTriangle, RotateCw } from 'lucide-react';
import { getPharmacyItems, pickPharmacyItem } from '../../redux/medicineSlice';
import { serverReason } from '../patientTabs/prescriptionHelpers';
import ModalShell, { MODAL_SUBMIT_CLASS } from '../common/ModalShell';

// PICK FROM THE HOSPITAL PHARMACY (owner, 12 Oct 2026). Where the tenant's
// medicines source is "From pharmacy" or "Both", the doctor adds items to
// their list from the pharmacy's own list. Each item says only whether the
// pharmacy has it in stock — never how many — and an item already on the
// doctor's list shows as added.

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 30;

export function StockBadge({ stock, status, t }) {
    if (status === 'discontinued') {
        return (
            <span data-testid="stock-badge-discontinued" className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-50 text-red-600 text-[11px] font-semibold">
                {t('medicinesTab.pharmacy_discontinued', 'No longer in the pharmacy')}
            </span>
        );
    }
    return stock === 'in' ? (
        <span data-testid="stock-badge-in" className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
            {t('medicinesTab.in_stock', 'In stock')}
        </span>
    ) : (
        <span data-testid="stock-badge-out" className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[11px] font-semibold">
            {t('medicinesTab.out_of_stock', 'Out of stock')}
        </span>
    );
}

function PharmacyPickModal({ doctorId, onClose }) {
    const { t, i18n } = useTranslation(['medicinesTab']);
    const lang = i18n?.language || 'en';
    const dispatch = useDispatch();

    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [type, setType] = useState('all');
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pickingId, setPickingId] = useState(null);

    useEffect(() => {
        const next = search.trim();
        if (next === query) return undefined;
        const timer = setTimeout(() => setQuery(next), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search, query]);

    const load = async (nextPage = 1) => {
        if (!doctorId) return;
        setLoading(true);
        setError(null);
        const res = await dispatch(getPharmacyItems({ doctorId, search: query, type, page: nextPage, limit: PAGE_SIZE }));
        setLoading(false);
        if (getPharmacyItems.fulfilled.match(res)) {
            const rows = res.payload?.data || [];
            setItems((prev) => (nextPage > 1 ? [...prev, ...rows.filter((r) => !prev.some((p) => p.medicineId === r.medicineId))] : rows));
            setPage(Number(res.payload?.page) || nextPage);
            setTotalPages(Number(res.payload?.totalPages) || 0);
        } else {
            if (nextPage === 1) setItems([]);
            setError(res.payload || 'failed');
        }
    };

    useEffect(() => {
        load(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doctorId, query, type]);

    const markPicked = (pharmacyMedicineId, entryId) => setItems((prev) => prev.map((i) => (
        i.medicineId === pharmacyMedicineId ? { ...i, pickedAs: entryId || i.pickedAs || true } : i
    )));

    const handlePick = async (item) => {
        if (pickingId || item.pickedAs) return;
        setPickingId(item.medicineId);
        const res = await dispatch(pickPharmacyItem({ doctorId, pharmacyMedicineId: item.medicineId }));
        setPickingId(null);
        if (pickPharmacyItem.fulfilled.match(res)) {
            markPicked(item.medicineId, res.payload?.data?.medicineId);
            toast.success(res.payload?.adopted
                ? t('medicinesTab.pick_adopted', '“{{name}}” on your list now follows the pharmacy.', { name: item.name })
                : t('medicinesTab.pick_added', '“{{name}}” added to your list.', { name: item.name }));
        } else if (res.payload?.code === 'ALREADY_IN_LIST') {
            markPicked(item.medicineId, res.payload?.data?.medicineId);
        } else {
            toast.error(serverReason(res.payload, t('medicinesTab.pick_failed', 'Could not add this item.'), lang));
        }
    };

    const hasMore = page > 0 && page < totalPages;

    return (
        <ModalShell
            title={t('medicinesTab.pick_title', 'Pick from the pharmacy')}
            onClose={onClose}
            closeLabel={t('medicinesTab.close', 'Close')}
            size="2xl"
            testId="pharmacy-pick-modal"
            closeTestId="pharmacy-pick-close"
            footer={(
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="pharmacy-pick-done"
                    className={MODAL_SUBMIT_CLASS}
                >
                    {t('medicinesTab.pick_done', 'Done')}
                </button>
            )}
        >
                <p className="text-xs text-gray-500">
                    {t('medicinesTab.pick_subtitle', "Items on your organisation's pharmacy list. Add the ones you prescribe.")}
                </p>
                <div className="pt-3 flex flex-col sm:flex-row gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input
                            type="text"
                            data-testid="pharmacy-pick-search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value.replace(/[<>]/g, ''))}
                            placeholder={t('medicinesTab.pick_search_placeholder', 'Search by name, salt or maker')}
                            className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-[#4ABDBD]"
                        />
                    </div>
                    <div className="flex gap-1.5">
                        {['all', 'medicine', 'product'].map((tp) => (
                            <button
                                key={tp}
                                type="button"
                                data-testid={`pharmacy-pick-type-${tp}`}
                                aria-pressed={type === tp}
                                onClick={() => setType(tp)}
                                className={`px-3 py-2 rounded-xl text-sm font-medium border transition cursor-pointer ${
                                    type === tp
                                        ? 'bg-[#4ABDBD] text-white border-[#4ABDBD]'
                                        : 'bg-white text-gray-600 border-gray-200 hover:border-[#4ABDBD]'
                                }`}
                            >
                                {tp === 'all'
                                    ? t('medicinesTab.filter_all', 'All')
                                    : tp === 'medicine'
                                    ? t('medicinesTab.filter_medicine', 'Medicines')
                                    : t('medicinesTab.filter_product', 'Products')}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="py-3">
                    {loading && items.length === 0 ? (
                        <p className="py-8 flex items-center justify-center gap-2 text-sm text-gray-500" data-testid="pharmacy-pick-loading">
                            <Loader2 size={14} className="animate-spin" /> {t('medicinesTab.pick_loading', 'Loading the pharmacy list…')}
                        </p>
                    ) : error && items.length === 0 ? (
                        <div role="alert" data-testid="pharmacy-pick-error" className="py-8 flex flex-col items-center gap-2 text-center">
                            <AlertTriangle className="text-red-400" size={28} />
                            <p className="text-sm text-red-600">
                                {serverReason(error, t('medicinesTab.pick_load_failed', 'The pharmacy list could not be loaded.'), lang)}
                            </p>
                            <button
                                type="button"
                                onClick={() => load(1)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-200 text-red-700 text-sm font-semibold hover:bg-red-50 cursor-pointer"
                            >
                                <RotateCw size={13} /> {t('medicinesTab.retry', 'Retry')}
                            </button>
                        </div>
                    ) : items.length === 0 ? (
                        <p className="py-8 text-center text-sm text-gray-500" data-testid="pharmacy-pick-empty">
                            {query
                                ? t('medicinesTab.pick_no_match', 'Nothing on the pharmacy list matches “{{query}}”.', { query })
                                : t('medicinesTab.pick_empty', 'The pharmacy list is empty.')}
                        </p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {items.map((item) => {
                                const isProduct = item.type === 'product';
                                const added = Boolean(item.pickedAs);
                                const busy = pickingId === item.medicineId;
                                const detail = [item.genericName, item.strength].filter(Boolean).join(' · ');
                                return (
                                    <li key={item.medicineId} data-testid={`pharmacy-pick-item-${item.medicineId}`} className="py-3 flex items-center gap-3">
                                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isProduct ? 'bg-amber-50 text-amber-500' : 'bg-[#4ABDBD]/10 text-[#2D8A8A]'}`}>
                                            {isProduct ? <Package size={17} /> : <Pill size={17} />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-gray-800 text-sm truncate" title={item.name}>{item.name}</p>
                                            {(detail || item.manufacturer) && (
                                                <p className="text-xs text-gray-500 truncate">
                                                    {[detail, item.manufacturer].filter(Boolean).join(' — ')}
                                                </p>
                                            )}
                                            <div className="mt-1"><StockBadge stock={item.stock} t={t} /></div>
                                        </div>
                                        {added ? (
                                            <span data-testid={`pharmacy-pick-added-${item.medicineId}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-gray-100 text-gray-500 text-xs font-semibold">
                                                <Check size={13} /> {t('medicinesTab.pick_on_list', 'On your list')}
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                data-testid={`pharmacy-pick-add-${item.medicineId}`}
                                                onClick={() => handlePick(item)}
                                                disabled={Boolean(pickingId)}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-[#4ABDBD]/10 text-[#2D8A8A] text-xs font-semibold hover:bg-[#4ABDBD]/20 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                                                {t('medicinesTab.pick_add', 'Add')}
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    {hasMore && (
                        <div className="pt-2 text-center">
                            <button
                                type="button"
                                data-testid="pharmacy-pick-load-more"
                                onClick={() => load(page + 1)}
                                disabled={loading}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] text-sm font-semibold bg-white hover:bg-[#4ABDBD]/10 cursor-pointer disabled:opacity-60"
                            >
                                {loading && <Loader2 size={14} className="animate-spin" />}
                                {t('medicinesTab.load_more', 'Load more')}
                            </button>
                        </div>
                    )}
                </div>

        </ModalShell>
    );
}

export default PharmacyPickModal;
